// src/common/verification/analyze-stage.js
import { extractErrorMessage } from '../utils/error-utils.js';
import { updateCitationUI } from '../ui.js';

/**
 * Analyze a citation for congruence with the claim.
 * Handles caching and API calls.
 * 
 * @param {Object} citation - The citation object to analyze
 * @param {Object} resolveResult - The resolution result from resolve stage
 * @param {string} claim - The claim text
 * @param {string} footnoteQuote - The footnote quote (optional)
 * @param {Object} options - Configuration options
 * @param {string} options.apiBase - API base URL
 * @param {Function} options.getAuthHeaders - Function to get auth headers
 * @param {boolean} options.isWebApp - Whether running in web app mode
 * @param {Object} options.commonPayload - Common payload for API calls
 * @param {Object} options.cachedCongruence - Cached congruence result (optional)
 * @param {boolean} options.skipCache - Whether to skip cache (for "Check Again")
 * @param {HTMLElement} options.resultsContainer - Container element for UI updates (optional)
 * @returns {Promise<Object>} Analysis/congruence result
 */
export async function analyzeCitation(citation, resolveResult, claim, footnoteQuote, options = {}) {
  const {
    apiBase,
    getAuthHeaders,
    isWebApp = false,
    commonPayload = {},
    footnote = null,
    parsedCitations = null,
    cachedCongruence = null,
    skipCache = false,
    resultsContainer = null,
  } = options;

  // Check for cached congruence (skip if Check Again)
  if (!skipCache && cachedCongruence) {
    console.log('[AnalyzeStage] Using cached congruence for citation', citation.id);
    if (resultsContainer) {
      updateCitationUI(resultsContainer, citation.id, { state: 'complete', message: 'Congruence check complete (cached)' });
    }
    return {
      ...citation,
      ...resolveResult,
      congruence_result: {
        label: cachedCongruence.label,
        calibration: cachedCongruence.confidence,
        rationale: cachedCongruence.rationale
      },
      label: cachedCongruence.label,
      confidence: cachedCongruence.confidence,
      rationale: cachedCongruence.rationale
    };
  }

  if (resultsContainer) {
    updateCitationUI(resultsContainer, citation.id, { state: 'analyzing', message: 'Analyzing source...' });
  }

  const endpoint = isWebApp ? '/web/audit/analyze' : '/audit/analyze';
  const requestBody = JSON.stringify({
    citation,
    resolve_result: resolveResult,
    claim,
    footnote_quote: footnoteQuote,
    ...commonPayload,
    footnote: footnote ? {
      id: footnote.id,
      text: footnote.text,
      rich_text: footnote.rich_text,
      context: footnote.context,
      full_context: footnote.full_context,
      quote: footnote.quote,
    } : null,
    parsed_citations: parsedCitations,
  });

  const sendAnalyzeRequest = async (authOptions = {}) => {
    const authHeaders = await getAuthHeaders(authOptions);
    return fetch(`${apiBase}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: requestBody,
    });
  };

  let res = await sendAnalyzeRequest();
  if (res.status === 401) {
    console.warn(`[AnalyzeStage] 401 for ${citation.id}; refreshing token and retrying once`);
    res = await sendAnalyzeRequest({ forceRefresh: true });
  }

  const data = await res.json();
  if (!res.ok) {
    // Handle insufficient credits (402 Payment Required)
    if (res.status === 402) {
      const detail = data.detail || {};
      const message = detail.message || 'Insufficient credits to perform analysis.';
      
      // Refresh credits display to show current balance
      if (typeof window.refreshCredits === 'function') {
        window.refreshCredits();
      }
      
      throw new Error(`💳 ${message}`);
    }
    throw new Error(extractErrorMessage(data) || `Failed to analyze ${citation.id}`);
  }

  // Refresh credits after successful operation (costs money)
  if (typeof window.refreshCredits === 'function') {
    window.refreshCredits();
  }

  return { ...citation, ...resolveResult, ...data };
}

export async function analyzeJointGroup(groupId, citations, memberResults, claim, footnote, options = {}) {
  const { apiBase, getAuthHeaders, isWebApp = false, commonPayload = {} } = options;
  const endpoint = isWebApp ? '/web/audit/analyze_joint' : '/audit/analyze_joint';
  const body = JSON.stringify({
    ...commonPayload,
    joint_evaluation_group: groupId,
    citations,
    member_results: memberResults,
    claim,
    footnote: footnote ? { id: footnote.id, text: footnote.text } : null,
  });
  const send = async (authOptions = {}) => fetch(`${apiBase}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders(authOptions)) },
    body,
  });
  let response = await send();
  if (response.status === 401) response = await send({ forceRefresh: true });
  const data = await response.json();
  if (!response.ok || data.status === 'error') {
    throw new Error(extractErrorMessage(data) || `Failed to analyze joint group ${groupId}`);
  }
  return data.joint_group_result;
}
